import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RestService } from '../../services/rest.service';
import { GtmAnalyticsService } from '../../services/gtm-analytics.service';
import { StorageService } from '../../services/storage.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ErrorReportingService } from '../../services/error-reporting.service';

@Component({
  selector: 'app-payment-success',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './payment-success.component.html',
  styleUrl: './payment-success.component.scss',
})
export class PaymentSuccessComponent implements OnInit {
  private maxRetries = 60;
  private retryDelay = 2000;
  private retryCount = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private restService: RestService,
    private gtmAnalytics: GtmAnalyticsService,
    private storageService: StorageService,
    private reporter: ErrorReportingService
  ) {}

  async ngOnInit() {
    const scanId = this.route.snapshot.queryParams['scan_id'];
    const sessionId = this.route.snapshot.queryParams['session_id'];
    const token = this.route.snapshot.queryParams['token'];

    console.log('PaymentSuccessComponent: initialized with params', {
      scanId,
      sessionId,
      token,
    });

    if (!scanId) {
      console.error('PaymentSuccessComponent: no scan_id provided');
      await this.router.navigate(['/app/new-test']);
      return;
    }

    if (!sessionId && !token) {
      console.log(
        'PaymentSuccessComponent: no session_id or token, redirecting to initial-results'
      );
      await this.router.navigate(['/app/trial-result', scanId]);
      return;
    }

    this.checkPaymentStatus(scanId);
  }

  private async checkPaymentStatus(scanId: string) {
    try {
      const response: any = await this.restService.request({
        path: `payment/check-status/${scanId}`,
        method: 'GET',
      });

      if (response.paymentStatus === 'paid') {
        this.reporter.logEvent('payment.confirmed', { tries: this.retryCount });
        this.trackPaymentCompletion(response);
        await this.router.navigate(['/app/results', scanId]);
        return;
      }

      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        setTimeout(() => this.checkPaymentStatus(scanId), this.retryDelay);
      } else {
        // Exhausted the polling budget without a 'paid' status.
        this.reporter.report(
          `payment status poll exhausted after ${this.maxRetries} tries`,
          'payment',
        );
        await this.router.navigate(['/app/trial-result', scanId]);
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      this.reporter.report(error, 'payment');
      await this.router.navigate(['/app/trial-result', scanId]);
    }
  }

  private trackPaymentCompletion(response: any): void {
    const priceData = this.storageService.getPriceData();
    
    if (!priceData) {
      console.log('PaymentSuccessComponent: No price data available for tracking');
      return;
    }

    const planType = response.planType || 'single';
    const price = planType === 'bundle' ? parseFloat(priceData.bundlePrice) : parseFloat(priceData.price);
    const planName = planType === 'bundle' ? 'Bundle' : 'Single';

    this.gtmAnalytics.trackCompletedPayment({
      value: price,
      currency: 'USD',
      plan_type: 'one_time',
      plan_name: planName,
    });
  }
}
